<script>
import ContactService from '@/services/ContactService';
import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
import moment from 'moment/moment';
import AdditionalVisitorDetails from '@/components/additionalVisitors/details/AdditionalVisitorDetails.vue';
import { mapState } from 'vuex';
import SocialBookingSlotsService from '@/services/SocialBookingSlotsService';
import { getDateOfYearDayAndTime } from '@/util/helpers';
import SocialVisitsService from '@/services/SocialVisitsService';
import { ERROR_CODES } from '@/util/errorCodes';
import RestrictedSocialBookingDatesService from '@/services/RestrictedSocialBookingDatesService';
import VisitLocationsService from '@/services/VisitLocationsService';
import VisitorDetails from '@/components/visitors/details/VisitorDetails.vue';
import VisitorsPreviousVisits from '@/views/visitorDetails/VisitorsPreviousVisits.vue';

export default {
  name: 'BookSocialVisit',
  components: { VisitorsPreviousVisits, VisitorDetails },
  data() {
    return {
      loading: true,
      prisonerNumber: null,
      visitors: [],
      visitor: null,
      visitLocations: [],
      selectedVisitLocation: null,
      additionalVisitors: [],
      fields: [
        {
          key: 'checkbox',
          label: '',
          thStyle: { width: '10%' },
        },
        {
          key: 'fullName',
          label: 'Name',
        },
        {
          key: 'birthDateAndAge',
          label: 'DOB',
        },
        {
          key: 'verificationStatus',
          label: 'Verification status',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      currentPage: 1,
      selectedAdditionalVisitorIds: [],
      bookingSlotsWithStock: [],
      bookingSlotDates: [],
      bookingSlotsGrouped: [],
      selectedBookingSlotId: null,
      selectedBookingSlotDate: null,
      restrictedBookingDates: [],
      selectedOver18Count: 0,
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
  },
  async mounted() {
    try {
      await Promise.all([
        this.getVisitLocations(),
        this.getRestrictedSocialDates(),
      ]);
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getRestrictedSocialDates() {
      try {
        const response = await RestrictedSocialBookingDatesService.getFutureRestrictedDates();
        this.restrictedBookingDates = response.data;
      } catch (e) {
        console.error('Error getting restricted social booking dates: ', e);
        this.$toast.error('Error getting restricted social booking dates', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getVisitLocations() {
      try {
        if (!this.prison) {
          await this.$store.dispatch('prison/getPrison');
        }
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        console.log('>>> getVisitLocations: ', response);
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations');
        this.$toast.error('Error getting visit locations', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }
    },
    async getBookingSlotsWithStock() {
      try {
        const response = await SocialBookingSlotsService.getBookingSlotsWithStockForLocation(this.prison.prison_id, this.selectedVisitLocation._id)
        this.bookingSlotsWithStock = response.data;
        const flatten = {};
        this.bookingSlotsWithStock.forEach(bookingSlotStock => {
          if (flatten[bookingSlotStock.day_of_week] === null || flatten[bookingSlotStock.day_of_week] === undefined) {
            flatten[bookingSlotStock.day_of_week] = { day_of_week: bookingSlotStock.day_of_week, slots: [] };
          }
          flatten[bookingSlotStock.day_of_week].slots.push({
            booking_slot_id: bookingSlotStock._id,
            name: bookingSlotStock.name,
            slot_begin: bookingSlotStock.slot_begin,
            slot_end: bookingSlotStock.slot_end,
            visit_location: bookingSlotStock.visit_location,
          });
        });
        Object.keys(flatten).map(key => this.bookingSlotsGrouped.push(flatten[key]));
        // Order the grouped slots
        this.bookingSlotsGrouped.forEach(slot => {
          // eslint-disable-next-line no-param-reassign
          slot.slots = slot.slots.sort((bs1, bs2) => {
            // Fake year and day, as we only care about the slot_begin time
            const year = 2000;
            const day = 1;
            const bs1StartTime = getDateOfYearDayAndTime(year, day, bs1.slot_begin);
            const bs2StartTime = getDateOfYearDayAndTime(year, day, bs2.slot_begin);
            return bs1StartTime.getTime() - bs2StartTime.getTime();
          });
        });
        // Sort booking slots so they always show Mon-Sun
        const weekDaySorter = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 7,
        };
        this.bookingSlotsGrouped = this.bookingSlotsGrouped.sort((bsg1, bsg2) => {
          const bs1Day = bsg1.day_of_week.toLowerCase();
          const bs2Day = bsg2.day_of_week.toLowerCase();
          return weekDaySorter[bs1Day] - weekDaySorter[bs2Day];
        });
      } catch (e) {
        console.error('Error getting booking slots: ', e);
        this.$toast.error('Error getting booking slots', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    clearDateSelection() {
      // Clear selected date
      this.selectedBookingSlotDate = null;
      this.bookingSlotDates = [];
      // Update date list
      if (this.selectedBookingSlotId) {
        let dates = [];
        this.bookingSlotsWithStock.forEach(bookingSlotWithStock => {
          if (bookingSlotWithStock._id === this.selectedBookingSlotId) {
            dates = bookingSlotWithStock.stock;
          }
        });
        // Order dates, closest first
        dates.sort((d1, d2) => new Date(d2).getTime() - new Date(d1).getTime());
        this.bookingSlotDates = dates;
      }
    },
    isDateRestricted() {
      const dateToCheck = this.selectedBookingSlotDate;
      if (!dateToCheck) {
        return false;
      }

      let restricted = false;
      this.restrictedBookingDates.forEach(rDate => {
        const before = moment(dateToCheck).isSameOrBefore(rDate.end_date);
        const after = moment(dateToCheck).isSameOrAfter(rDate.start_date);

        if (before && after) {
          restricted = true;
        }
      });

      return restricted;
    },
    async searchForContacts() {
      try {
        if (!this.prisonerNumber || !this.prisonerNumber.length) {
          this.$toast.error(`Please enter a ${this.$options.filters.term('prisoner')} number`)
          return;
        }
        this.loading = true;
        this.visitor = null;
        const response = await ContactService.getPrisonerContacts(this.prisonerNumber);
        if (!response.data || !response.data.length) {
          this.$swal('No contacts found!', `Please check the ${this.$options.filters.term('prisoner')} number and try again.`)
        } else {
          this.$swal('Great news!', `${response.data.length} ${response.data.length <= 1 ? 'contact has' : 'contacts have'} been found. Please select one from the visitor drop down menu.`, 'success');
        }
        this.visitors = response.data;
      } catch (e) {
        console.error('Error getting prisoner: ', e);
        this.$toast.error('Error getting prisoner', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    getBookingSlotsWithStockForLocation() {
      this.bookingSlotsWithStock = [];
      this.bookingSlotsGrouped = [];
      this.clearDateSelection();
      this.getBookingSlotsWithStock();
    },
    async selectVisitor() {
      try {
        if (!this.visitor) {
          return;
        }
        this.additionalVisitors = [];
        await this.getAdditionalVisitors();
      } catch (e) {
        console.error('Error selecting contact: ', e);
        this.$toast.error('Error selecting contact', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    async getAdditionalVisitors() {
      try {
        const response = await AdditionalVisitorsService.getAdditionalVisitorsForVisitorSocialVisit(this.visitor.user.user_id);
        this.additionalVisitors = response.data.map(additionalVisitor => ({
            fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
            birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
            ...additionalVisitor,
          }));
      } catch (e) {
        console.error('Error getting additional visitors: ', e);
        this.$toast.error('Error getting additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async toggleAdditionalVisitor(item) {
      const isOver18 = moment(new Date()).diff(item.dob, 'years') >= 18;

      const selected = this.selectedAdditionalVisitorIds.includes(item.additional_visitor_id);
      if (selected) {
        this.$toast.error('Additional Visitor removed');
       this.selectedAdditionalVisitorIds = this.selectedAdditionalVisitorIds.filter(id => id !== item.additional_visitor_id);

        // Decrement count if visitor was over 18
        if (isOver18) {
          this.selectedOver18Count -= 1;
        }
      } else {
        // Only allow 2 additional visitors over 18
        if (this.selectedOver18Count >= 2 && isOver18) {
          this.$toast.error('Maximum of 2 additional visitors over 18 allowed!', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
          return;
        }

        let add = true;
        if (this.isUnder18(item) && !item.safeguarded_at_prison) {
          add = await this.$swal({
            title: 'Are you sure?',
            text: `${item.firstname} ${item.lastname} is under 18 and has not completed the safeguarding checks. Are you sure you want to add them as an additional visitor?`,
            icon: 'warning',
            buttons: true,
          });
        }
        if (!add) {
          return;
        }
        this.$toast.success('Additional Visitor added');
        this.selectedAdditionalVisitorIds.push(item.additional_visitor_id);

        // Increment count if visitor is over 18
        if (isOver18) {
          this.selectedOver18Count += 1;
        }
      }
    },
    isUnder18(additionalVisitor) {
      return parseInt(moment(new Date()).diff(additionalVisitor.dob, 'years'), 10) < 18;
    },
    viewDetails(additionalVisitor) {
      this.$modal.show(AdditionalVisitorDetails, {
        additional_visitor_id: additionalVisitor.additional_visitor_id,
      }, {
        height: 'auto',
        classes: 'full-screen-modal',
        scrollable: true,
      }, {
        'before-close': () => {
          this.getSelectedVisitorsAdditionalVisitors();
        },
      });
    },
    async getSelectedVisitorsAdditionalVisitors() {
      try {
        this.loading = true;
        const response = await AdditionalVisitorsService.getAdditionalVisitors(this.visitor.user.user_id);
        this.additionalVisitors = response.data.map(additionalVisitor => ({
            fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
            birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
            ...additionalVisitor,
          }));
      } catch (e) {
        console.error('Error getting selected additional visitors: ', e);
        this.$toast.error('Error getting selected additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    canBookVisit() {
      return this.prisonerNumber && this.visitor && this.selectedBookingSlotDate && this.selectedBookingSlotId && this.selectedVisitLocation;
    },
    async bookVisit() {
      try {
        this.loading = true;
        const response = await SocialVisitsService.bookVisitForVisitor({
          visitor_id: this.visitor.user.user_id,
          contact_id: this.visitor._id,
          visit_location_id: this.selectedVisitLocation._id,
          booked_for_booking_slot_id: this.selectedBookingSlotId,
          booked_for_booking_slot_date: this.selectedBookingSlotDate,
          additional_visitors_ids: this.selectedAdditionalVisitorIds || [],
        });
        const visit = response.data;
        this.$toast.success('Visit booked', {
          toastClassName: ['toast-std', 'success-toast'],
        });
        await this.$store.dispatch('socialVisits/setVisit', visit);
        await this.$router.push(`/social-visits/${visit._id}/details`);
      } catch (err) {
        console.error('Error booking visit for visitor: ', err);

        const error = err && err.response && err.response.data && err.response.data.error;
        const errorMsg = error ? err.response.data.error : '';
        let msg = 'Something went wrong booking the visit, please refresh the page and try again';

        if (errorMsg === ERROR_CODES.VISITOR.NOT_VERIFIED) {
          msg = 'Cannot book visit as the visitor is not verified';
        } else if (errorMsg === ERROR_CODES.VISITOR.BANNED_ON_DATE) {
          msg = 'Cannot book visit as visitor is banned on the chosen booking slot';
        } else if (errorMsg === ERROR_CODES.VISIT_STOCK.NONE_LEFT) {
          msg = 'Cannot book visit as the chosen booking slot has no stock left'
        } else if (errorMsg === ERROR_CODES.VISIT.PRISONER_BOOKED_ON_SLOT) {
          msg = `Cannot book visit as ${this.prisonerNumber} is already booked on another visit during the chosen booking slot`;
        } else if (errorMsg === ERROR_CODES.ADDITIONAL_VISITOR.NOT_VERIFIED) {
          msg = 'Unable to book with those additional visitors as one or more is not marked as verified';
        }

        this.$toast.error(msg, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    getStatus(item) {
      return item.verification_status === 'AwaitingVerification' ? 'Awaiting Verification' : item.verification_status;
    },
  },
}
</script>

<template>
  <div>
    <h2>Book Social Visit</h2>
    <div v-if="loading">
      <div class="col text-center mt-5">
        <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
      </div>
    </div>
    <b-row v-else class="mt-2">
      <b-col cols="6">
        <b-card>
          <!--Prisoner Number-->
          <label for="prisoner-number">
            {{ 'prisoner' | term(true) }} Number
          </label>
          <b-input-group class="mb-1">
            <b-form-input id="prisoner-number" v-model="prisonerNumber" />
            <template #append>
              <b-btn variant="primary" :disabled="loading" @click="searchForContacts">
                Search
              </b-btn>
            </template>
          </b-input-group>

          <!--Visit Locations-->
          <section>
            <label for="visit-locations">
              Visit Location
            </label>
            <b-form-select
              id="visit-locations"
              v-model="selectedVisitLocation"
              class="mb-1"
              :disabled="visitLocations.length <= 0 || loading"
              @change="getBookingSlotsWithStockForLocation"
            >
              <b-form-select-option v-for="item in visitLocations" :key="item._id" :value="item">
                {{ item.name }}
              </b-form-select-option>
            </b-form-select>
            <div v-if="visitLocations.length <= 0" class="mb-1">
              <strong style="color: red">
                You must create a social visit location first.
              </strong>
              <router-link :to="{ name: 'visit-locations' }">
                Click here to create a visit location
              </router-link>
            </div>
          </section>

          <!--Visitor-->
          <label for="visitor">
            Visitor
          </label>
          <b-form-select
            id="visitor"
            v-model="visitor"
            class="mb-1"
            :disabled="visitors.length <= 0 || loading"
            @change="selectVisitor"
          >
            <b-form-select-option v-for="item in visitors" :key="item._id" :value="item">
              {{ item.user.firstname }} {{ item.user.lastname }}
            </b-form-select-option>
          </b-form-select>

          <!--Additional Visitors-->
          <div v-if="visitor && additionalVisitors && additionalVisitors.length > 0">
            <label for="additional-visitors">
              Additional Visitors
            </label>
            <b-table
              class="data-table mb-1"
              :current-page="currentPage"
              :fields="fields"
              :items="additionalVisitors"
              hover
              responsive
              show-empty
            >
              <template #cell(checkbox)="data">
                <button
                  v-if="selectedAdditionalVisitorIds && !selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                  class="btn btn-success btn-sm"
                  @click="toggleAdditionalVisitor(data.item)"
                >
                  <span class="fa fa-plus" />
                </button>
                <button
                  v-if="selectedAdditionalVisitorIds && selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                  class="btn btn-danger btn-sm"
                  @click="toggleAdditionalVisitor(data.item)"
                >
                  <span class="fa fa-minus" />
                </button>
              </template>
              <template #cell(birthDateAndAge)="data">
                {{ data.item.birthDateAndAge }}
                <span
                  v-if="isUnder18(data.item)"
                  class="badge badge-pill"
                  :class="[data.item.safeguarded_at_prison ? 'badge-success' : 'badge-danger ']"
                >
                  UNDER 18
                </span>
              </template>
              <template #cell(verificationStatus)="data">
                {{ getStatus(data.item) }}
              </template>
              <template #cell(actions)="data">
                <button class="btn btn-primary btn-sm" @click="viewDetails(data.item)">
                  <span class="fas fa-search" /> View
                </button>
              </template>
            </b-table>
          </div>

          <!--Booking Slot-->
          <label for="booking-slot">
            Booking Slot
          </label>
          <b-form-select
            id="booking-slot"
            v-model="selectedBookingSlotId"
            class="mb-1"
            :disabled="this.bookingSlotsGrouped.length <= 0"
            @change="clearDateSelection"
          >
            <b-form-select-option-group
              v-for="(groupedSlot, index) in bookingSlotsGrouped"
              :key="index"
              :label="groupedSlot.day_of_week"
            >
              <b-form-select-option
                v-for="slot in groupedSlot.slots"
                :key="slot.booking_slot_id"
                :value="slot.booking_slot_id"
              >
                {{ slot.name }} {{ slot.slot_begin }} - {{ slot.slot_end }}
              </b-form-select-option>
            </b-form-select-option-group>
          </b-form-select>

          <!--Booking Date-->
          <label for="booking-date">
            Booking Date
          </label>
          <p v-if="isDateRestricted()" class="text-danger">This booking date overlaps with a restricted date period.</p>
          <b-form-select
            v-model="selectedBookingSlotDate"
            class="mb-2"
            :disabled="!selectedBookingSlotId"
          >
            <b-form-select-option
              v-for="(date, index) in bookingSlotDates"
              :key="index" :value="date.date"
              :disabled="date.remaining === 0"
            >
              {{ date.date | formatDate }} ({{ date.remaining }} {{ date.remaining > 1 ? 'tables' : 'table' }} available)
            </b-form-select-option>
          </b-form-select>

          <b-btn
            :disabled="!canBookVisit()"
            class="float-right"
            variant="primary"
            @click="bookVisit"
          >
            Book Visit
          </b-btn>
        </b-card>
      </b-col>
      <b-col v-if="visitor" cols="6">
        <div class="row">
          <div class="col-12">
            <visitor-details :visitor_user_id="visitor.user.user_id" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <visitors-previous-visits
              :visitor_user_id="visitor.user.user_id"
              :prisoner_number="prisonerNumber"
            />
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
